// import { ReactNode } from "react";
import styled, { css } from "styled-components";

import {
  UilCheck,
  UilArrowRight,
  UilPlus,
  UilSignin,
  UilEllipsisV,
  UilPen,
  UilTrash,
  UilTimes,
  UilEye,
  UilEyeSlash,
  UilSync,
  UilAngleLeftB,
  UilAngleLeft,
  UilAngleRightB,
  UilAngleRight,
  UilImport,
  // UilDollarSign,
  UilLink,
  UilSearch,
  UilTimesCircle,
  UilUpload,
  UilLayerGroup,
  UilExchange,
  UilExclamationCircle,
  UilExport,
  UilCopy,
} from "@iconscout/react-unicons";

import NFEicon from "../../assets/images/pdf.svg";
import XMLicon from "../../assets/images/xml.svg";
import payableIcon from "../../assets/images/payableCreate.svg";
import goodsExpressBlueAndClockIcon from "../../assets/images/goodsStocksEntering.svg";
import goodsExpressRedIcon from "../../assets/images/goodsFailed.svg";
import goodsExpressGreenIcon from "../../assets/images/goodsStocksEntered.svg";

import { fullRotate } from "../../utils/animations";
import SVG from "../ui/SVG";
import Tooltip from "./Tooltip";
import { TText } from "./TableGrid";
import Spinner from "./Spinner";
import { useRef } from "react";
import { Property } from "csstype";

const ACTION_BUTTONS = [
  "nfe",
  "xml",
  "payableYellow",
  "payableGreen",
  "goodsReceiptYellow",
  "goodsReceiptGreen",
  "goodsExpressBlue",
  "goodsExpressBlueAndClock",
  "goodsExpressRed",
  "goodsExpressGreen",
  "arrowRight",
  "exchange",
  "danger2025",
  "warning2025",
];

const Container = styled.button<{
  $variant: TVariants;
  $status: "enabled" | "disabled";
  $icon: TButtonIconVariants;
  $iconType?: TWithIconVariants | "string";
  $isLoading?: boolean;
  $shouldFontWeightNotBeBold?: boolean;
}>`
  border-radius: 8px;
  padding: ${({ $icon, $variant }) =>
    $icon === "onlyIcon"
      ? $variant === "onlyIcon"
        ? "0px"
        : `8px`
      : $variant === "primary"
      ? `9px 16px`
      : $variant === "secondary"
      ? "7px 16px"
      : `${$variant}`.includes("2025") || $variant === "link"
      ? "8px 12px"
      : "8px 16px"};

  font-family: ${({ $variant, $shouldFontWeightNotBeBold }) =>
    `${$variant}`.includes("2025") ||
    $variant === "link" ||
    $shouldFontWeightNotBeBold
      ? "var(--font-paragraph)"
      : "var(--font-paragraph-bold)"};
  font-size: ${({ $iconType, $variant }) =>
    $iconType === "string" || `${$variant}`.includes("2025")
      ? "12px"
      : $variant === "link"
      ? "14px"
      : "16px"};
  line-height: 22.4px;

  ${({ $icon, $iconType }) =>
    $icon === "onlyIcon" && $iconType === "string"
      ? "width: 32px; height: 32px;"
      : "height: fit-content;"}

  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  transition: background 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  box-shadow: ${({ $iconType, $variant }) =>
    $iconType && !ACTION_BUTTONS.includes($iconType) && $variant !== "onlyIcon"
      ? `0px 2px 6px 0px rgba(57, 60, 77, 0.15)`
      : `box-shadow: 0 0 0 0px var(--color-main-blue);`};

  svg {
    width: ${({ $icon, $iconType }) =>
      $icon === "onlyIcon"
        ? $iconType &&
          [
            "nfe",
            "xml",
            "goodsExpressBlue",
            "goodsExpressBlueAndClock",
            "goodsExpressRed",
            "goodsExpressGreen",
          ].includes($iconType)
          ? "20px"
          : "14px"
        : "20px"};
    height: ${({ $icon, $iconType }) =>
      $icon === "onlyIcon"
        ? $iconType &&
          [
            "nfe",
            "xml",
            "goodsExpressBlue",
            "goodsExpressBlueAndClock",
            "goodsExpressRed",
            "goodsExpressGreen",
          ].includes($iconType)
          ? "20px"
          : "14px"
        : "20px"};
    margin-right: ${({ $icon }) => ($icon === "onlyIcon" ? "unset" : "5px")};

    ${({ $isLoading, $variant, $icon }) =>
      $isLoading &&
      css`
        path {
          fill: ${["primary", "white", "green", "orange", "red"].includes(
            $variant
          )
            ? $icon !== "sync" && "var(--color-light-0)"
            : $variant === "secondary"
            ? "var(--color-dark-blue-600)"
            : $variant === "outline"
            ? "var(--color-main-rose)"
            : `${$variant}`.includes("danger")
            ? "var(--color-aux-danger-700)"
            : `${$variant}`.includes("warning")
            ? "var(--color-aux-warning-700)"
            : undefined};
        }
        animation: ${fullRotate} 1.3s linear infinite;
      `}
  }

  ${({ $variant, $icon, $iconType }) => {
    let variantConfig;
    switch ($variant) {
      case "primary":
        variantConfig =
          "background: var(--color-gradient-rose);" +
          "color: var(--color-light-0);";
        break;
      case "secondary":
        if ($iconType && ACTION_BUTTONS.includes($iconType)) {
          switch ($iconType) {
            case "nfe":
              variantConfig =
                `height: 40px;` +
                `width: 40px;` +
                `background: var(--color-blue-100);` +
                `border-radius: 100%;` +
                // `padding: 9px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-main-blue);` +
                `svg { fill: var(--color-main-blue);width: 18px; height: 23px;}`;
              break;
            case "xml":
              variantConfig =
                `height: 40px;` +
                `width: 40px;` +
                `background: var(--color-blue-100);` +
                `border-radius: 100%;` +
                // `padding: 9px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-main-blue);` +
                `svg { fill: var(--color-main-blue);width: 18px; height: 23px;}`;
              break;
            case "payableGreen":
              variantConfig =
                `background: var(--color-aux-success-100);` +
                `border-radius: 100%;` +
                `svg {& .stroke-path {stroke: var(--color-aux-success-100);}fill: var(--color-aux-success-700); width: 18px; height: 23px;}` +
                `width: 40px;` +
                `height: 40px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-aux-success-700);`;
              break;
            case "payableYellow":
              variantConfig =
                `background: var(--color-aux-warning-100);` +
                `border-radius: 100%;` +
                `svg {& .stroke-path {stroke: var(--color-aux-warning-100);}fill: var(--color-aux-warning-700); width: 18px; height: 23px;}` +
                `width: 40px;` +
                `height: 40px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-aux-warning-700);`;
              break;
            case "goodsReceiptGreen":
              variantConfig =
                `background: var(--color-aux-success-100);` +
                `border-radius: 100%;` +
                `svg {fill: var(--color-aux-success-700); width: 18px; height: 23px;}` +
                `width: 40px;` +
                `height: 40px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-aux-success-700);`;
              break;
            case "goodsReceiptYellow":
              variantConfig =
                `background: var(--color-aux-warning-100);` +
                `border-radius: 100%;` +
                `svg {fill: var(--color-aux-warning-700); width: 18px; height: 23px;}` +
                `width: 40px;` +
                `height: 40px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-aux-warning-700);`;
              break;
            case "goodsExpressBlue":
              variantConfig =
                `background: var(--color-blue-100);` +
                `border-radius: 100%;` +
                `svg {fill: var(--color-main-blue); width: 18px; height: 22px;}` +
                `width: 40px;` +
                `height: 40px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-main-blue);`;
              break;
            case "goodsExpressBlueAndClock":
              variantConfig =
                `background: var(--color-blue-100);` +
                `border-radius: 100%;` +
                `svg {fill: var(--color-blue-800);  width: 18px; height: 22px; & .circle-path { fill: var(--color-blue-100); }
                }` +
                `width: 40px;` +
                `height: 40px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-main-blue);`;
              break;
            case "goodsExpressRed":
              variantConfig =
                `background: var(--color-aux-danger-100);` +
                `border-radius: 100%;` +
                `svg {& .circle-path {fill: var(--color-aux-danger-100);}fill: var(--color-aux-danger-700); width: 18px; height: 22px;}` +
                `width: 40px;` +
                `height: 40px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-aux-danger-700);`;
              break;
            case "goodsExpressGreen":
              variantConfig =
                `background: var(--color-aux-success-100);` +
                `border-radius: 100%;` +
                `svg {
                fill: var(--color-aux-success-700); width: 18px; height: 22px;
                & .circle-path { fill: var(--color-aux-success-100); }
                }` +
                `width: 40px;` +
                `height: 40px;` +
                `border-style: solid;` +
                `border-width: 1px;` +
                `border-color: var(--color-aux-success-700);`;
              break;
            case "exchange":
              variantConfig =
                `background: var(--color-aux-warning-100);` +
                `border-radius: 100%;` +
                `svg {fill: var(--color-aux-warning-700); width: 22px; height: 22px;}` +
                `padding: 3px;` +
                `border: 2px solid transparent;`;
              break;
            case "arrowRight":
              variantConfig =
                `background: red;` +
                `border: none;` +
                `svg {fill: var(--color-dark-blue-400); width: 22px; height: 22px;}`;
              break;
          }
        } else {
          variantConfig =
            "background: var(--color-light-0);" +
            "color: var(--color-dark-blue-600);";
          variantConfig +=
            // $icon === "onlyIcon"
            /* ? */ "border: 1px solid var(--color-dark-blue-300);";
          // : "border: 1px solid var(--color-dark-blue-600);"
        }
        break;
      case "outline":
        variantConfig =
          "background: var(--color-light-0);" +
          "color: var(--color-main-rose);";
        variantConfig +=
          $icon === "onlyIcon"
            ? "border: 1px solid var(--color-dark-blue-300);"
            : "border: 1px solid var(--color-main-rose);";
        break;
      case "link":
        variantConfig =
          "background: transparent;" + "color: var(--color-text-link);";
        break;
      case "danger":
        variantConfig =
          "background: var(--color-light-0);" +
          "color: var(--color-aux-danger-700);" +
          "border: 1px solid var(--color-aux-danger-700);";
        break;
      case "danger2025":
        variantConfig =
          "svg {fill: var(--color-aux-danger-700);}" +
          "background: var(--color-aux-danger-100);" +
          "color: var(--color-text-paragraph);" +
          "border: 1px solid var(--color-aux-danger-700);";
        break;
      case "warning2025":
        variantConfig =
          "svg {fill: var(--color-aux-warning-700);}" +
          "background: var(--color-aux-warning-100);" +
          "color: var(--color-text-paragraph);" +
          "border: 1px solid var(--color-aux-warning-700);";
        break;
      case "white":
        variantConfig =
          "background: transparent;" +
          "border-radius: 4px;" +
          "padding: 16px 8px;" +
          "color: var(--color-light-0);";
        break;
      case "green":
        variantConfig =
          "background: var(--color-gradient-aux-success);" +
          "color: var(--color-light-0);";
        break;
      case "orange":
        variantConfig =
          "background: var(--color-gradient-aux-warning);" +
          "color: var(--color-light-0);";
        break;
      case "red":
        variantConfig =
          "background: var(--color-gradient-aux-danger);" +
          "color: var(--color-light-0);";
        break;
      case "onlyIcon":
        variantConfig =
          "background: transparent;" +
          "border: none;" +
          "svg {fill: var(--color-main-blue);}";
        break;
    }
    return variantConfig;
  }}

  &:disabled {
    pointer-events: none;

    ${({ $variant, $icon, $iconType }) => {
      let variantConfig;
      switch ($variant) {
        case "primary":
          variantConfig =
            "background: linear-gradient(hsla(341, 100%, 60%, 0.3), hsla(342, 70%, 52%, 0.3));";
          break;
        case "secondary":
          if ($iconType && ACTION_BUTTONS.includes($iconType)) {
            switch ($iconType) {
              case "nfe":
                variantConfig =
                  `background: var(--color-light-100);` +
                  `border-color: var(--color-light-200);` +
                  `svg {fill: var(--color-dark-blue-300)}`;
                break;
              case "xml":
                variantConfig =
                  `background: var(--color-light-100);` +
                  `border-color: var(--color-light-200);` +
                  `svg {fill: var(--color-dark-blue-300)}`;
                break;
              case "goodsReceiptGreen":
              case "goodsReceiptYellow":
              case "goodsExpressBlue":
              case "goodsExpressRed":
              case "goodsExpressBlueAndClock":
              case "goodsExpressGreen":
                variantConfig =
                  `background: var(--color-light-100);` +
                  `border-color: var(--color-light-200);` +
                  `svg {fill: var(--color-dark-blue-300);}`;
                break;
              case "payableGreen":
              case "payableYellow":
                variantConfig =
                  `background: var(--color-light-100);` +
                  `border-color: var(--color-light-200);` +
                  `svg {fill: var(--color-dark-blue-300);}`;
                break;
              case "arrowRight":
                variantConfig =
                  `background: transparent;` +
                  `border: none;` +
                  `svg {fill: var(--color-dark-blue-400);}`;
                break;
            }
          } else {
            variantConfig = "color: var(--color-dark-blue-400);";
            variantConfig +=
              $icon === "onlyIcon"
                ? "border: 1px solid var(--color-dark-blue-300);"
                : "border-color: var(--color-dark-blue-400);";
          }
          break;
        case "outline":
          variantConfig = "color: var(--color-rose-500);";
          variantConfig +=
            $icon === "onlyIcon"
              ? "border: 1px solid var(--color-dark-blue-300);"
              : "border-color: var(--color-rose-500);";
          break;
        case "link":
          variantConfig = "color: var(--color-text-link-disabled);";
          break;
        case "danger":
          variantConfig =
            "color: hsla(0, 71%, 55%, 0.5);" +
            "border-color: hsla(0, 71%, 55%, 0.5);";
          break;
        case "white":
          variantConfig = "color: hsla(0, 0%, 100%, 0.5);";
          break;
        case "green":
          variantConfig =
            "background: linear-gradient(180deg, #2CF193 0%, #0DBC68 100%)";
          break;
        case "orange":
          variantConfig =
            "background: linear-gradient(180deg, #FFB84E 0%, #F29914 100%);";
          break;
        case "red":
          variantConfig =
            "background: linear-gradient(180deg, #FF5454 0%, #DE3939 100%);          ";
          break;
      }
      return variantConfig;
    }}
  }

  &:hover {
    ${({ $variant, $iconType }) => {
      let variantConfig;
      switch ($variant) {
        case "primary":
          variantConfig =
            "background: linear-gradient(hsla(341, 100%, 60%, 0.9), hsla(342, 70%, 52%, 0.9));";
          break;
        case "secondary":
          if ($iconType && ACTION_BUTTONS.includes($iconType)) {
            switch ($iconType) {
              case "nfe":
                variantConfig =
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-main-blue);` +
                  `border-color: var(--color-main-blue);` +
                  `background: var(--color-blue-100);`;
                break;
              case "xml":
                variantConfig =
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-main-blue);` +
                  `border-color: var(--color-main-blue);` +
                  `background: var(--color-blue-100);`;
                break;
              case "payableGreen":
                variantConfig =
                  `background: var(--color-aux-success-100);` +
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-aux-success-700);` +
                  `border-color: var(--color-aux-success-700);`;
                break;
              case "payableYellow":
                variantConfig =
                  `background: var(--color-aux-warning-100);` +
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-aux-warning-700);` +
                  `border-color: var(--color-aux-warning-700);`;
                break;
              case "goodsReceiptGreen":
                variantConfig =
                  `background: var(--color-aux-success-100);` +
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-aux-success-700);` +
                  `border-color: var(--color-aux-success-700);`;
                break;
              case "goodsReceiptYellow":
                variantConfig =
                  `background: var(--color-aux-warning-100);` +
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-aux-warning-700);` +
                  `border-color: var(--color-aux-warning-700);`;
                break;
              case "goodsExpressBlue":
                variantConfig =
                  `background: var(--color-blue-100);` +
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-main-blue);` +
                  `border-color: var(--color-main-blue);`;
                break;
              case "goodsExpressBlueAndClock":
                variantConfig =
                  `background: var(--color-blue-100);` +
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-main-blue);` +
                  // `border-width: 2px;` +
                  `border-color: var(--color-main-blue);`;
                break;
              case "goodsExpressRed":
                variantConfig =
                  `background: var(--color-aux-danger-100);` +
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-aux-danger-700);` +
                  `border-color: var(--color-aux-danger-700);`;
                break;
              case "goodsExpressGreen":
                variantConfig =
                  `background: var(--color-aux-success-100);` +
                  `border-style: solid;` +
                  `box-shadow: 0 0 0 1px var(--color-aux-success-700);` +
                  `border-color: var(--color-aux-success-700);`;
                break;
              case "arrowRight":
                variantConfig = `background: transparent;` + `border: none;`;
                break;
            }
          } else {
            variantConfig = "background: var(--color-light-50);";
          }
          break;
        case "outline":
          variantConfig = "background: var(--color-light-100);";
          break;
        case "link":
          variantConfig =
            "background: transparent;" + "color: var(--color-text-link-hover);";
          break;
        case "danger":
          variantConfig = "background: var(--color-light-100);";
          break;
        case "danger2025":
          variantConfig =
            "svg {fill: var(--color-aux-danger-700);}" +
            "background: var(--color-aux-danger-100);" +
            "color: var(--color-text-paragraph);" +
            `box-shadow: 0 0 0 1px var(--color-aux-danger-700);`;
          break;
        case "warning2025":
          variantConfig =
            "svg {fill: var(--color-aux-warning-700);}" +
            "background: var(--color-aux-warning-100);" +
            "color: var(--color-text-paragraph);" +
            `box-shadow: 0 0 0 1px var(--color-aux-warning-700);`;
          break;
        case "white":
          variantConfig = "background: hsla(0, 0%, 96%, 0.15);";
          break;
        case "green":
          variantConfig =
            "background: linear-gradient(180deg, #2CF193 0%, #0DBC68 100%)," +
            "linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1));";
          break;
        case "orange":
          variantConfig =
            "background: linear-gradient(0deg, #F29914, #F29914)," +
            "linear-gradient(180deg, #FFB84E 0%, #F29914 100%)," +
            "linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1))";
          break;

        case "red":
          variantConfig =
            "background: linear-gradient(0deg, #DE3939, #DE3939)," +
            "linear-gradient(180deg, #FF5454 0%, #DE3939 100%)," +
            "linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1));";
          break;
        case "onlyIcon":
          variantConfig = "svg {fill: var(--color-blue-800);}";
      }
      return variantConfig;
    }}
  }

  &:active {
    ${({ $variant, $iconType }) => {
      let variantConfig;
      switch ($variant) {
        case "primary":
          variantConfig = "background: var(--color-rose-800);";
          break;
        case "secondary":
          if ($iconType && ACTION_BUTTONS.includes($iconType)) {
            switch ($iconType) {
              case "nfe":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-main-blue);` +
                  `padding: 0px;` +
                  `background: var(--color-main-blue);` +
                  `svg { fill: var(--color-light-0);}`;
                break;
              case "xml":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-main-blue);` +
                  `padding: 0px;` +
                  `background: var(--color-main-blue);` +
                  `svg { fill: var(--color-light-0);}`;
                break;
              case "payableGreen":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-aux-success-700);` +
                  `svg {fill: var(--color-light-0); & .stroke-path {stroke: var(--color-aux-success-700);}}` +
                  `background: var(--color-aux-success-700);`;
                break;
              case "payableYellow":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-aux-warning-700);` +
                  `svg {fill: var(--color-light-0); & .stroke-path {stroke: var(--color-aux-warning-700);}}` +
                  `background: var(--color-aux-warning-700);`;
                break;
              case "goodsReceiptGreen":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-aux-success-700);` +
                  `svg {fill: var(--color-light-0); & .stroke-path{stroke: var(--color-aux-success-700);}}` +
                  `background: var(--color-aux-success-700);`;
                break;
              case "goodsReceiptYellow":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-aux-warning-700);` +
                  `svg {fill: var(--color-light-0); & .stroke-path{stroke: var(--color-aux-warning-700);}}` +
                  `background: var(--color-aux-warning-700);`;
                break;
              case "goodsExpressBlue":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-main-blue);` +
                  `svg {fill: var(--color-light-0); & .stroke-path{stroke: var(--color-main-blue);}}` +
                  `background: var(--color-main-blue);`;
                break;
              case "goodsExpressBlueAndClock":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-main-blue);` +
                  `svg {fill: var(--color-light-0); & .circle-path{fill: var(--color-main-blue);}}` +
                  `background: var(--color-main-blue);`;
                break;
              case "goodsExpressRed":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-aux-danger-700);` +
                  `svg {fill: var(--color-light-0); & .circle-path{fill: var(--color-aux-danger-700);}}` +
                  `background: var(--color-aux-danger-700);`;
                break;
              case "goodsExpressGreen":
                variantConfig =
                  `box-shadow: 0 0 0 1px var(--color-aux-success-700);` +
                  `svg {fill: var(--color-light-0); & .circle-path { fill: var(--color-aux-success-700); }
                }` +
                  `background: var(--color-aux-success-700);`;
                break;
            }
          } else {
            variantConfig = "background: var(--color-light-100);";
          }
          break;

        case "outline":
          variantConfig = "background: var(--color-light-200);";
          break;
        case "danger":
          variantConfig = "background: var(--color-light-200);";
          break;
        case "link":
          variantConfig =
            "background: transparent;" + "color: var(--color-text-link-hover);";
          break;
        case "danger2025":
          variantConfig =
            "svg {fill: var(--color-light-0);}" +
            "background: var(--color-aux-danger-700);" +
            "color: var(--color-light-0);" +
            `box-shadow: 0 0 0 1px var(--color-aux-danger-700);`;
          break;
        case "warning2025":
          variantConfig =
            "svg {fill: var(--color-light-0);}" +
            "background: var(--color-aux-warning-700);" +
            "color: var(--color-light-0);" +
            `box-shadow: 0 0 0 1px var(--color-aux-warning-700);`;
          break;
        case "white":
          variantConfig = "background: hsla(0, 0%, 4%, 0.25);";
          break;
        case "green":
          variantConfig = "background: var(--color-aux-success-800);";
          break;
        case "orange":
          variantConfig = "background: var(--color-aux-warning-800);";
          break;
        case "red":
          variantConfig = "background: var(--color-aux-danger-800);";
          break;
      }
      return variantConfig;
    }}
  }
`;

type TVariants =
  | "primary"
  | "secondary"
  | "outline"
  | "danger"
  | "danger2025"
  | "warning2025"
  | "white"
  | "green"
  | "orange"
  | "red"
  | "link"
  | "onlyIcon";

export type TWithIconVariants =
  | "checkmark"
  | "arrowRight"
  | "plus"
  | "logout"
  | "ellipsisV"
  | "pen"
  | "trash"
  | "times"
  | "timesCircle"
  | "times-red"
  | "eye"
  | "eyeSlash"
  | "sync"
  | "angleLeftB"
  | "angleLeft"
  | "angleRightB"
  | "angleRight"
  | "nfe"
  | "xml"
  | "goodsReceiptYellow"
  | "payableYellow"
  | "goodsReceiptGreen"
  | "payableGreen"
  | "goodsExpressBlue"
  | "goodsExpressBlueAndClock"
  | "goodsExpressRed"
  | "goodsExpressGreen"
  | "link"
  | "search"
  | "upload"
  | "layerGroup"
  | "brokenLink"
  | "exchange"
  | "import"
  | "export"
  | "exclamationCircle"
  | "copy";

export type TButtonIconVariants = "noIcon" | "onlyIcon" | TWithIconVariants;

type TWithIcon = {
  loading?: boolean;
  icon: TWithIconVariants;
  iconType?: never;
  iconPosition: "left" | "right";
  text: string;
};

type TNoIcon = {
  loading?: boolean;
  icon: "noIcon";
  iconType?: never;
  iconPosition?: never;
  text: string;
};

type TOnlyIcon<T extends TWithIconVariants | "string"> = T extends "string"
  ? {
      loading?: boolean;
      icon: "onlyIcon";
      iconPosition?: never;

      iconType: "string";
      text: string;
    }
  : {
      loading?: boolean;
      icon: "onlyIcon";
      iconPosition?: never;

      iconType: TWithIconVariants;
      text?: never;
    };

type TCommonProps = {
  id?: string;
  shouldFontWeightNotBeBold?: boolean;
  "data-testid"?: string;
  className?: string;
  onClick?: (props?: any) => void;
  onBlur?: (e: React.FocusEvent<HTMLButtonElement, Element>) => "show" | "hide";
  style?: React.CSSProperties;
  disabled?: boolean;
  variant: TVariants;
  isLoading?: boolean;
  tooltip?: {
    size?: "default" | "large";
    text: TText[];
    customTextAlignment?: Property.TextAlign;
    customTopPosition?: number;
    scrollableRef?: React.MutableRefObject<HTMLTableSectionElement | null>;
  };
  role?: "button" | "submit";
};

export type TButtonProps<Icon extends TButtonIconVariants> =
  Icon extends "noIcon"
    ? TNoIcon & TCommonProps
    : Icon extends "onlyIcon"
    ? TOnlyIcon<TWithIconVariants | "string"> & TCommonProps
    : TWithIcon & TCommonProps;

const Button: React.FC<TButtonProps<TButtonIconVariants>> = ({
  // children,
  style,
  disabled,
  variant,
  onClick,
  onBlur,
  icon,
  iconType,
  iconPosition,
  text,
  id,
  shouldFontWeightNotBeBold,
  "data-testid": dataTestId,
  className,
  isLoading,
  tooltip,
  role,
}: TButtonProps<TButtonIconVariants>) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const buttonText = text ?? "";

  const handleIconPositionLeft = (a: JSX.Element, b: string) => (
    <>
      {a}
      {icon !== "onlyIcon" && <>&nbsp;</>}
      {b}
    </>
  );
  const handleIconPositionRight = (a: string, b: JSX.Element) => (
    <>
      {a}
      {icon !== "onlyIcon" && <>&nbsp;</>}
      {b}
    </>
  );

  const switchCaseHandler = (icon: TWithIconVariants | "string") => {
    if (isLoading) {
      return icon === "sync" ? <UilSync /> : <Spinner className="spinner" />;
    } else {
      switch (icon) {
        case "checkmark":
          return <UilCheck />;
        case "arrowRight":
          return <UilArrowRight />;
        case "plus":
          return <UilPlus />;
        case "logout":
          return <UilSignin style={{ transform: "scaleX(-1)" }} />;
        case "ellipsisV":
          return <UilEllipsisV />;
        case "pen":
          return <UilPen />;
        case "trash":
          return <UilTrash />;
        case "times":
        case "times-red":
          return <UilTimes />;
        case "timesCircle":
          return <UilTimesCircle />;
        case "eye":
          return <UilEye />;
        case "eyeSlash":
          return <UilEyeSlash />;
        case "sync":
          return <UilSync />;
        case "angleLeftB":
          return <UilAngleLeftB />;
        case "angleLeft":
          return <UilAngleLeft />;
        case "angleRightB":
          return <UilAngleRightB />;
        case "angleRight":
          return <UilAngleRight />;
        case "string":
          return;
        case "nfe":
          return <SVG className="nfe" src={NFEicon} />;
        case "xml":
          return <SVG className="xml" src={XMLicon} />;
        case "goodsReceiptYellow":
          return <UilImport />;
        case "goodsReceiptGreen":
          return <UilImport />;
        case "goodsExpressBlue":
          return <UilImport />;
        case "goodsExpressBlueAndClock":
          return (
            <SVG className="goodsExpress" src={goodsExpressBlueAndClockIcon} />
          );
        case "goodsExpressRed":
          return <SVG className="goodsExpress" src={goodsExpressRedIcon} />;
        case "goodsExpressGreen":
          return <SVG className="goodsExpress" src={goodsExpressGreenIcon} />;
        case "payableYellow":
          return <SVG className="payable" src={payableIcon} />;
        case "payableGreen":
          return <SVG className="payable" src={payableIcon} />;
        case "link":
          return <UilLink />;
        case "search":
          return <UilSearch />;
        case "upload":
          return <UilUpload />;
        case "import":
          return <UilImport />;
        case "layerGroup":
          return <UilLayerGroup />;
        case "exchange":
          return <UilExchange />;
        case "exclamationCircle":
          return <UilExclamationCircle />;
        case "export":
          return <UilExport />;
        case "copy":
          return <UilCopy />;
      }
    }
  };

  const renderContentByPickedIcon = () => {
    let iconElement;
    if (icon === "onlyIcon") {
      iconElement = switchCaseHandler(iconType);
    } else if (icon !== "noIcon") {
      iconElement = switchCaseHandler(icon);
    }
    return iconPosition === "left"
      ? handleIconPositionLeft(iconElement as JSX.Element, buttonText)
      : handleIconPositionRight(buttonText, iconElement as JSX.Element);
  };

  const onClickHandler = (props?: any) => {
    buttonRef.current?.focus();
    if (onClick) onClick(props);
  };

  return tooltip && tooltip.text.length > 0 ? (
    <Tooltip
      size={tooltip.size}
      text={tooltip.text}
      customTextAlignment={tooltip.customTextAlignment}
    >
      <Container
        ref={buttonRef}
        $isLoading={isLoading}
        $iconType={iconType}
        $icon={icon}
        $status={disabled ? "disabled" : "enabled"}
        id={id}
        data-testid={dataTestId}
        className={className}
        $variant={variant}
        onClick={onClickHandler}
        onBlur={onBlur}
        role="button"
        style={style && style}
        disabled={disabled || isLoading}
      >
        {renderContentByPickedIcon()}
      </Container>
    </Tooltip>
  ) : (
    <Container
      ref={buttonRef}
      $isLoading={isLoading}
      $iconType={iconType}
      $icon={icon}
      $status={disabled ? "disabled" : "enabled"}
      id={id}
      data-testid={dataTestId}
      className={className}
      $variant={variant}
      $shouldFontWeightNotBeBold={shouldFontWeightNotBeBold}
      onClick={onClickHandler}
      onBlur={onBlur}
      role={role ?? "button"}
      style={style && style}
      disabled={disabled || isLoading}
    >
      {renderContentByPickedIcon()}
    </Container>
  );
};

export default Button;
